import { InboxOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Descriptions, Select, Space, Table, Typography, Upload } from "antd";
import { useEffect, useState } from "react";

import { useImportRoster } from "../../api/candidates";
import { useNames } from "../../api/names";
import type { Group, RosterImportResponse } from "../../api/types";

const HEADERS = "考生姓名、省份、准考证号、考点名称、考点区域、考场、座位号、密码";

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

/** Upload the registration office's roster (xlsx or csv), validate it on the server, then import once it is clean. */
export function RosterPage() {
  const importRoster = useImportRoster();
  const { catalog } = useNames();
  const [group, setGroup] = useState<Group>();
  const [file, setFile] = useState<{ name: string; contentBase64: string; size: number }>();
  const [checked, setChecked] = useState<RosterImportResponse>();
  const [error, setError] = useState<string>();
  const groups = catalog.data?.groups ?? [];

  useEffect(() => {
    if (group === undefined && groups.length > 0) setGroup(groups[0].id);
  }, [group, groups]);

  const run = async (dryRun: boolean) => {
    if (!file || !group) return;
    setError(undefined);
    try {
      setChecked(await importRoster.mutateAsync({ filename: file.name, contentBase64: file.contentBase64, group, dryRun }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "请求失败");
    }
  };

  const validated = checked?.ok === true && checked.dryRun;
  const imported = checked?.ok === true && !checked.dryRun;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", maxWidth: 960 }}>
      <Alert
        type="info"
        showIcon
        message="名单格式"
        description={<>
          <div>xlsx 或 csv，按表头名称识别列：<Typography.Text code>{HEADERS}</Typography.Text>。</div>
          <div>重新导入同一准考证号会覆盖其信息并清除考生当前会话。</div>
        </>}
      />
      <Space wrap>
        <span>组别</span>
        <Select<Group> style={{ width: 140 }} value={group} onChange={(value) => { setGroup(value); setChecked(undefined); }}
          options={groups.map((item) => ({ value: item.id, label: item.name }))} />
      </Space>
      <Upload.Dragger
        accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        maxCount={1}
        showUploadList={false}
        beforeUpload={(uploaded) => {
          const reader = new FileReader();
          reader.onload = () => {
            setFile({ name: uploaded.name, contentBase64: toBase64(reader.result as ArrayBuffer), size: uploaded.size });
            setChecked(undefined);
            setError(undefined);
          };
          reader.readAsArrayBuffer(uploaded);
          return false;
        }}
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">点击或拖入考生名单（xlsx 或 csv）</p>
        <p className="ant-upload-hint">{file ? `已选择 ${file.name}（${Math.round(file.size / 1024)} KB）` : "报名系统导出并加上密码列的文件"}</p>
      </Upload.Dragger>
      <Space>
        <Button onClick={() => void run(true)} disabled={!file || !group} loading={importRoster.isPending}>校验</Button>
        <Button type="primary" onClick={() => void run(false)} disabled={!validated} loading={importRoster.isPending}>导入</Button>
      </Space>
      {error && <Alert type="error" showIcon message={error} />}
      {checked && (
        <Descriptions size="small" bordered column={2}>
          <Descriptions.Item label="考生行数">{checked.rowCount}</Descriptions.Item>
          <Descriptions.Item label="新建考场">{checked.newRooms} 个</Descriptions.Item>
          <Descriptions.Item label="新建考点区域">{checked.newRegions.length ? checked.newRegions.join("、") : "无"}</Descriptions.Item>
          <Descriptions.Item label="新建考点">{checked.newSites.length ? checked.newSites.join("、") : "无"}</Descriptions.Item>
        </Descriptions>
      )}
      {checked && !checked.ok && (
        <Card title={`校验未通过，共 ${checked.problemCount} 处问题`}>
          <Table
            rowKey={(row, index) => `${row.line}-${index}`}
            size="small"
            pagination={{ pageSize: 20 }}
            dataSource={checked.problems}
            columns={[
              { title: "行", dataIndex: "line", width: 80, render: (line: number) => line > 0 ? line : "—" },
              { title: "问题", dataIndex: "message" }
            ]}
          />
        </Card>
      )}
      {validated && <Alert type="success" showIcon message={`校验通过，共 ${checked.rowCount} 行，可以导入。`} />}
      {imported && checked.ok && !checked.dryRun && (
        <Alert
          type={checked.failed.length ? "warning" : "success"}
          showIcon
          message={`已导入 ${checked.imported} 人${checked.failed.length ? `，${checked.failed.length} 人写入失败：${checked.failed.join("、")}` : ""}`}
        />
      )}
    </Space>
  );
}
